import angular, { IHttpService, IQService } from 'angular';
import { Services } from '@Shared.Angular/@types/services';
import { Guid } from '@Shared.Angular/@types/guid';
import IFormThemeOverview from '@Shared.Angular/@types/core/contracts/queryModel/formTheme/formThemeOverview';
import IFormThemeDetail from '@Shared.Angular/@types/core/contracts/queryModel/formTheme/formThemeDetail';
import { HttpStatusCodes } from '@Shared.Angular/@types/core/contracts/constants/httpStatusCodes';

export class FormThemesApiService {
  private readonly apiBaseUrl: string;
  public deferredFormThemes: angular.IDeferred<IFormThemeOverview[]>;
  public deferredPublishedFormThemes: angular.IDeferred<IFormThemeOverview[]>;
  public formThemesLoaded = false;
  private deferredFormThemeCache: Map<
    Guid,
    angular.IDeferred<IFormThemeDetail>
  >;

  constructor(
    private $http: IHttpService,
    private $q: IQService,
    private APP_CONFIG: Services.APP_CONFIG
  ) {
    this.apiBaseUrl = `${this.APP_CONFIG.apiBaseUrl}formthemes/`;
    this.deferredFormThemes = this.$q.defer<IFormThemeOverview[]>();
    this.deferredFormThemeCache = new Map<
      Guid,
      angular.IDeferred<IFormThemeDetail>
    >();
  }

  createFormTheme(request: IFormThemeDetail) {
    return this.$http.post<Guid>(this.apiBaseUrl, request).then((response) => {
      this.invalidateFormThemes();
      return response.data;
    });
  }

  updateFormTheme(id: Guid, request: IFormThemeDetail) {
    return this.$http.put<void>(`${this.apiBaseUrl}${id}`, request).then(() => {
      this.invalidateFormThemes();
    });
  }

  getFormThemes(forceRefresh = false) {
    if (!forceRefresh && this.deferredFormThemes) {
      return this.deferredFormThemes.promise;
    }

    this.deferredFormThemes = this.$q.defer<IFormThemeOverview[]>();

    this.$http
      .get<IFormThemeOverview[]>(this.apiBaseUrl)
      .then((response) => {
        const themes = response.data ?? [];
        this.formThemesLoaded = true;
        this.deferredFormThemes.resolve(themes);
      })
      .catch((error) => {
        this.deferredFormThemes.reject(error);
        this.deferredFormThemes = null;
        this.formThemesLoaded = false;
      });

    return this.deferredFormThemes.promise;
  }

  getPublishedFormThemes(forceRefresh = false) {
    if (!forceRefresh && this.deferredPublishedFormThemes) {
      return this.deferredPublishedFormThemes.promise;
    }

    this.deferredPublishedFormThemes = this.$q.defer<IFormThemeOverview[]>();

    this.$http
      .get<IFormThemeOverview[]>(this.apiBaseUrl + 'published')
      .then((response) => {
        const themes = response.data ?? [];
        this.deferredPublishedFormThemes.resolve(themes);
      })
      .catch((error) => {
        this.deferredPublishedFormThemes.reject(error);
        this.deferredPublishedFormThemes = null;
      });

    return this.deferredPublishedFormThemes.promise;
  }

  publishFormTheme(id: Guid) {
    return this.$http
      .put<void>(`${this.apiBaseUrl}${id}/publish`, {})
      .then(() => {
        this.invalidateFormThemes();
      });
  }

  unPublishFormTheme(id: Guid) {
    return this.$http
      .put<void>(`${this.apiBaseUrl}${id}/unpublish`, {})
      .then(() => {
        this.invalidateFormThemes();
      });
  }

  getFormTheme(themeId: Guid, forceRefresh = false) {
    const cachedDeferred = this.deferredFormThemeCache.get(themeId);

    if (!forceRefresh && cachedDeferred) {
      return cachedDeferred.promise;
    }

    const newDeferred = this.$q.defer<IFormThemeDetail>();
    this.deferredFormThemeCache.set(themeId, newDeferred);

    this.$http
      .get<IFormThemeDetail>(`${this.apiBaseUrl}${themeId}`)
      .then((response) => {
        const theme = response.data;
        newDeferred.resolve(theme);
      })
      .catch((error) => {
        newDeferred.reject(error);
        this.deferredFormThemeCache.delete(themeId);
      });

    return newDeferred.promise;
  }

  getPublishedFormTheme(themeId: Guid) {
    return this.$http
      .get<IFormThemeDetail>(`${this.apiBaseUrl}${themeId}/published`)
      .then((response) => response.data)
      .catch((error) => {
        if (error.status === HttpStatusCodes.NotFound) {
          // Return null if no published version exists
          return null;
        }
        throw error;
      });
  }

  deleteFormTheme(themeId: Guid) {
    return this.$http.delete<void>(`${this.apiBaseUrl}${themeId}`).then(() => {
      this.invalidateFormThemes();
    });
  }

  invalidateFormThemes() {
    this.formThemesLoaded = false;
    this.deferredFormThemes = this.$q.defer<IFormThemeOverview[]>();
  }
}

angular
  .module('flowingly.services')
  .factory('formThemesApiService', [
    '$http',
    '$q',
    'APP_CONFIG',
    ($http: IHttpService, $q: IQService, appConfig: Services.APP_CONFIG) =>
      new FormThemesApiService($http, $q, appConfig)
  ]);

export type FormThemesApiServiceType = InstanceType<
  typeof FormThemesApiService
>;
